import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';

export interface GoodDeed {
  id: string;
  text: string;
  day: number;
  timestamp: number;
}

export interface QuranProgress {
  pages: number;
  juz: number;
  lastSurah?: number;
  lastAyah?: number;
  completedSurahs?: number[];
}

export interface TrackerData {
  prayers: Record<number, string[]>;
  quranProgress: Record<number, QuranProgress>;
  fastedDays: number[];
  missedFastDays: number[];
  goodDeeds: GoodDeed[];
  dhikrCounts: Record<number, Record<string, number>>;
}

const defaultTrackerData: TrackerData = {
  prayers: {},
  quranProgress: {},
  fastedDays: [],
  missedFastDays: [],
  goodDeeds: [],
  dhikrCounts: {},
};

export const useTrackerData = () => {
  const { user, isGuest } = useAuth();
  const { toast } = useToast();
  const [trackerData, setTrackerData] = useState<TrackerData>(defaultTrackerData);
  const [isLoading, setIsLoading] = useState(true);

  // Load data
  useEffect(() => {
    setTrackerData(defaultTrackerData);
    setIsLoading(true);

    const loadData = async () => {
      if (!user && !isGuest) {
        setIsLoading(false);
        return;
      }

      if (isGuest) {
        const saved = localStorage.getItem('ramadanRenewalProgress');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            setTrackerData({ ...defaultTrackerData, ...parsed.trackerData });
          } catch {
            setTrackerData(defaultTrackerData);
          }
        }
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('tracker_status')
          .select('*')
          .eq('user_id', user.id);

        if (error) throw error;

        if (data && data.length > 0) {
          const newTrackerData: TrackerData = { ...defaultTrackerData };

          data.forEach((row) => {
            const dayNum = new Date(row.date).getDate(); // Simplified - in production use proper date handling

            // Prayers
            const prayers: string[] = [];
            if (row.salah_fajr) prayers.push('fajr');
            if (row.salah_dhuhr) prayers.push('dhuhr');
            if (row.salah_asr) prayers.push('asr');
            if (row.salah_maghrib) prayers.push('maghrib');
            if (row.salah_isha) prayers.push('isha');
            if (prayers.length > 0) {
              newTrackerData.prayers[dayNum] = prayers;
            }

            // Quran
            if (row.quran_pages > 0 || row.quran_juz > 0) {
              newTrackerData.quranProgress[dayNum] = {
                pages: row.quran_pages,
                juz: row.quran_juz
              };
            }

            // Fasting
            if (row.fasting_completed) {
              newTrackerData.fastedDays.push(dayNum);
            }
            if (row.fasting_missed) {
              newTrackerData.missedFastDays.push(dayNum);
            }

            // Dhikr
            if (row.dhikr_subhanallah > 0 || row.dhikr_alhamdulillah > 0 || 
                row.dhikr_allahuakbar > 0 || row.dhikr_salawat > 0) {
              newTrackerData.dhikrCounts[dayNum] = {
                subhanallah: row.dhikr_subhanallah,
                alhamdulillah: row.dhikr_alhamdulillah,
                allahuakbar: row.dhikr_allahuakbar,
                salawat: row.dhikr_salawat
              };
            }

            // Good deeds
            if (row.good_deeds && Array.isArray(row.good_deeds)) {
              const deeds = (row.good_deeds as unknown as GoodDeed[]).map((deed: GoodDeed) => ({
                ...deed,
                day: dayNum
              }));
              newTrackerData.goodDeeds.push(...deeds);
            }
          });

          setTrackerData(newTrackerData);
        }
      } catch (error) {
        console.error('Error loading tracker data:', error);
        toast({
          title: 'Error loading data',
          description: 'Your progress could not be loaded. Please try again.',
          variant: 'destructive'
        });
      }
      setIsLoading(false);
    };

    loadData();
  }, [user, isGuest, toast]);

  // Save data
  const updateTrackerData = useCallback(async (updates: Partial<TrackerData>) => {
    const newData = { ...trackerData, ...updates };
    setTrackerData(newData);

    if (isGuest || !user) {
      // Save to localStorage for guests
      const saved = localStorage.getItem('ramadanRenewalProgress');
      const existing = saved ? JSON.parse(saved) : {};
      localStorage.setItem('ramadanRenewalProgress', JSON.stringify({
        ...existing,
        trackerData: newData
      }));
      return;
    }

    // We'll save individual day updates to the database
    // This is handled by the specific update functions below
  }, [trackerData, user, isGuest]);

  const saveDayData = useCallback(async (day: number, data: TrackerData) => {
    if (isGuest || !user) {
      // Already saved to localStorage in updateTrackerData
      return;
    }

    // Create a date for this Ramadan day (using 2025 as example - should come from user profile)
    const ramadanStart = new Date('2025-03-01'); // Example start date
    const date = new Date(ramadanStart);
    date.setDate(date.getDate() + day - 1);
    const dateStr = date.toISOString().split('T')[0];

    const prayers = data.prayers[day] || [];
    const quran = data.quranProgress[day] || { pages: 0, juz: 0 };
    const dhikr = data.dhikrCounts[day] || {};
    const dayGoodDeeds = data.goodDeeds.filter(d => d.day === day);

    const upsertData = {
      user_id: user.id,
      date: dateStr,
      salah_fajr: prayers.includes('fajr'),
      salah_dhuhr: prayers.includes('dhuhr'),
      salah_asr: prayers.includes('asr'),
      salah_maghrib: prayers.includes('maghrib'),
      salah_isha: prayers.includes('isha'),
      quran_pages: quran.pages,
      quran_juz: quran.juz,
      fasting_completed: data.fastedDays.includes(day),
      fasting_missed: data.missedFastDays.includes(day),
      dhikr_subhanallah: dhikr.subhanallah || 0,
      dhikr_alhamdulillah: dhikr.alhamdulillah || 0,
      dhikr_allahuakbar: dhikr.allahuakbar || 0,
      dhikr_salawat: dhikr.salawat || 0,
      good_deeds: dayGoodDeeds
    };

    try {
      // Check if record exists first
      const { data: existing } = await supabase
        .from('tracker_status')
        .select('id')
        .eq('user_id', user.id)
        .eq('date', dateStr)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('tracker_status')
          .update({
            salah_fajr: prayers.includes('fajr'),
            salah_dhuhr: prayers.includes('dhuhr'),
            salah_asr: prayers.includes('asr'),
            salah_maghrib: prayers.includes('maghrib'),
            salah_isha: prayers.includes('isha'),
            quran_pages: quran.pages,
            quran_juz: quran.juz,
            fasting_completed: data.fastedDays.includes(day),
            fasting_missed: data.missedFastDays.includes(day),
            dhikr_subhanallah: dhikr.subhanallah || 0,
            dhikr_alhamdulillah: dhikr.alhamdulillah || 0,
            dhikr_allahuakbar: dhikr.allahuakbar || 0,
            dhikr_salawat: dhikr.salawat || 0,
            good_deeds: dayGoodDeeds as unknown as Record<string, never>[]
          })
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('tracker_status')
          .insert({
            user_id: user.id,
            date: dateStr,
            salah_fajr: prayers.includes('fajr'),
            salah_dhuhr: prayers.includes('dhuhr'),
            salah_asr: prayers.includes('asr'),
            salah_maghrib: prayers.includes('maghrib'),
            salah_isha: prayers.includes('isha'),
            quran_pages: quran.pages,
            quran_juz: quran.juz,
            fasting_completed: data.fastedDays.includes(day),
            fasting_missed: data.missedFastDays.includes(day),
            dhikr_subhanallah: dhikr.subhanallah || 0,
            dhikr_alhamdulillah: dhikr.alhamdulillah || 0,
            dhikr_allahuakbar: dhikr.allahuakbar || 0,
            dhikr_salawat: dhikr.salawat || 0,
            good_deeds: dayGoodDeeds as unknown as Record<string, never>[]
          });

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error saving tracker data:', error);
      toast({
        title: 'Error saving',
        description: 'Your progress could not be saved. Please try again.',
        variant: 'destructive'
      });
    }
  }, [user, isGuest, toast]);

  return {
    trackerData,
    isLoading,
    updateTrackerData,
    saveDayData,
    defaultTrackerData
  };
};
